import React from 'react';
import { useApplicationSyncContext } from '../../ApplicationSyncContext/useApplicationSyncContext';
import useAudioServerCommunicationContext from '../AudioServerCommunicationContextProvider/useAudioServerCommunicationContext';
import useEnqueueTrigger from '../SessionConnectionContextProvider/Hooks/useEnqueueTrigger';
import { AudioWebhookResponse } from '../SessionConnectionContextProvider/useSessionConnectionContext';
import useSessionInfoContext from '../SessionInfoContextProvider/useSessionInfoContext';
import useAudioWebsocketApiConnectionContext from '../WebsocketConnectionProviders/AudioWebsocketApiConnectionContext/useAudioWebsocketApiConnectionContext';
import useWebsocketApiConnectionContext from '../WebsocketConnectionProviders/WebsocketApiConnectionContext/useWebsocketApiConnectionContext';

function useHandleAudioMessages(lastJsonMessage: AudioWebhookResponse | null) {
  const enqueueTrigger = useEnqueueTrigger();
  const { setEndCause } = useSessionInfoContext();
  const { sendJsonMessage: sendToAudio } = useAudioServerCommunicationContext();

  React.useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.type) {
      switch (lastJsonMessage.type) {
        case 'sessionActivityTrigger':
          // eslint-disable-next-line no-case-declarations
          const subKey = lastJsonMessage.activate ? 'activate' : 'deactivate';
          enqueueTrigger(
            'activityChange',
            subKey,
            () => {
              sendToAudio({
                type: 'confirm-session-activity-change',
                date: Date.now() * 1e6,
                activate: lastJsonMessage.activate,
                detection: lastJsonMessage.detection,
              });
            },
          );
          break;

        case 'sessionTimeout':
          enqueueTrigger(
            'sessionTimeout',
            'body',
            () => { setEndCause('SessionTimeout'); },
          );
          break;

        default:
          break;
      }
    }
  }, [lastJsonMessage]);
}

export default function InteractionConfirmationWrapper({ children }: { children: React.ReactNode }) {
  const { lastJsonMessage: lastMessageFromAudioWebsocketApi } = useAudioWebsocketApiConnectionContext();
  const { lastJsonMessage: lastMessageFromWebsocketApi } = useWebsocketApiConnectionContext();

  const { isSynced } = useApplicationSyncContext();

  const relevantMessage = React.useMemo(() => {
    if (isSynced) {
      return lastMessageFromWebsocketApi as AudioWebhookResponse;
    }
    return lastMessageFromAudioWebsocketApi;
  }, [isSynced, lastMessageFromWebsocketApi, lastMessageFromAudioWebsocketApi]);

  useHandleAudioMessages(relevantMessage);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
